import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import timesheetSaga from './saga';
import { TimesheetState } from './types';
import { RequestStatus } from 'constants/API';

export const initialState: TimesheetState = {
  getAllProjectsStatus: RequestStatus.IDLE,
  getAllMembersStatus: RequestStatus.IDLE,
  getAssignProjectsStatus: RequestStatus.IDLE,

  requestApprovalStatus: RequestStatus.IDLE,
  cancelRequestStatus: RequestStatus.IDLE,
  approveRequestStatus: RequestStatus.IDLE,

  getApprovalListStatus: RequestStatus.IDLE,
  getTimesheetStatus: RequestStatus.IDLE,
  getDateRangeWorkReportStatus: RequestStatus.IDLE,

  addTimesheetStatus: RequestStatus.IDLE,
  updateTimesheetStatus: RequestStatus.IDLE,
  deleteTimesheetStatus: RequestStatus.IDLE,

  getTagStatus: RequestStatus.IDLE,
  addTagStatus: RequestStatus.IDLE,

  allProjects: [],
  allMembers: [],
  assignProjects: null,
  approvalList: null,
  timesheetList: [],
  dateRangeWorkReport: null,
  monthWorkReport: null,
  tagList: [],
  errorMess: [],
};

const slice = createSlice({
  name: 'timesheet',
  initialState,
  reducers: {
    getAllMembersRequest: (state, action: PayloadAction<any>) => {
      state.getAllMembersStatus = RequestStatus.REQUESTING;
    },
    getAllMembersSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.getAllMembersStatus = RequestStatus.SUCCESS;
        state.allMembers = action.payload;
      }
    },
    getAllMembersFail: (state, action: PayloadAction<any>) => {
      if (action) state.getAllMembersStatus = RequestStatus.ERROR;
    },

    getAllProjectsRequest: (state, action: PayloadAction<any>) => {
      state.getAllProjectsStatus = RequestStatus.REQUESTING;
    },
    getAllProjectsSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.getAllProjectsStatus = RequestStatus.SUCCESS;
        state.allProjects = action.payload;
      }
    },
    getAllProjectsFail: (state, action: PayloadAction<any>) => {
      if (action) state.getAllProjectsStatus = RequestStatus.ERROR;
    },

    getAssignProjectsRequest: (state, action: PayloadAction<any>) => {
      state.getAssignProjectsStatus = RequestStatus.REQUESTING;
    },
    getAssignProjectsSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.getAssignProjectsStatus = RequestStatus.SUCCESS;
        state.assignProjects = action.payload;
      }
    },
    getAssignProjectsFail: (state, action: PayloadAction<any>) => {
      if (action) state.getAssignProjectsStatus = RequestStatus.ERROR;
    },
    resetGetAssignProjectStatus: state => {
      state.getAssignProjectsStatus = RequestStatus.IDLE;
      state.assignProjects = null;
    },

    getApprovalListRequest: (state, action: PayloadAction<any>) => {
      state.getApprovalListStatus = RequestStatus.REQUESTING;
    },
    getApprovalListSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.getApprovalListStatus = RequestStatus.SUCCESS;
        let data = action?.payload?.data;
        data = data.map(v => {
          return {
            ...v,
            id: v?.project?.id + '_' + v?.member?.id,
          };
        });
        state.approvalList = { ...action?.payload, data };
      }
    },
    getApprovalListFail: (state, action: PayloadAction<any>) => {
      if (action) state.getApprovalListStatus = RequestStatus.ERROR;
    },

    approveRequestRequest: (state, action: PayloadAction<any>) => {
      state.approveRequestStatus = RequestStatus.REQUESTING;
    },
    approveRequestSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.approveRequestStatus = RequestStatus.SUCCESS;
      }
    },
    approveRequestFail: (state, action: PayloadAction<any>) => {
      if (action) state.approveRequestStatus = RequestStatus.ERROR;
    },

    requestApprovalRequest: (state, action: PayloadAction<any>) => {
      state.requestApprovalStatus = RequestStatus.REQUESTING;
    },
    requestApprovalSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.requestApprovalStatus = RequestStatus.SUCCESS;
      }
    },
    requestApprovalFail: (state, action: PayloadAction<any>) => {
      if (action) state.requestApprovalStatus = RequestStatus.ERROR;
    },

    cancelRequestRequest: (state, action: PayloadAction<any>) => {
      state.cancelRequestStatus = RequestStatus.REQUESTING;
    },
    cancelRequestSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.cancelRequestStatus = RequestStatus.SUCCESS;
      }
    },
    cancelRequestFail: (state, action: PayloadAction<any>) => {
      if (action) state.cancelRequestStatus = RequestStatus.ERROR;
    },

    getDateRangeWorkReportRequest: (state, action: PayloadAction<any>) => {
      if (action) state.getDateRangeWorkReportStatus = RequestStatus.REQUESTING;
    },
    getDateRangeWorkReportSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.getDateRangeWorkReportStatus = RequestStatus.SUCCESS;
        state.dateRangeWorkReport = action?.payload?.days;
      }
    },
    getDateRangeWorkReportFail: (state, action: PayloadAction<any>) => {
      if (action) state.getDateRangeWorkReportStatus = RequestStatus.ERROR;
    },

    getTimesheetRequest: (state, action: PayloadAction<any>) => {
      if (action) state.getTimesheetStatus = RequestStatus.REQUESTING;
    },
    getTimesheetSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.getTimesheetStatus = RequestStatus.SUCCESS;
        state.timesheetList = action.payload;
      }
    },
    getTimesheetFail: (state, action: PayloadAction<any>) => {
      if (action) state.getTimesheetStatus = RequestStatus.ERROR;
    },

    addTimesheetRequest: (state, action: PayloadAction<any>) => {
      if (action) {
        state.addTimesheetStatus = RequestStatus.REQUESTING;
        state.errorMess = [];
      }
    },
    addTimesheetSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.addTimesheetStatus = RequestStatus.SUCCESS;
        state.tagList = action.payload;
      }
    },
    addTimesheetFail: (state, action: PayloadAction<any>) => {
      if (action) {
        state.addTimesheetStatus = RequestStatus.ERROR;
        state.errorMess = action.payload.errorMess;
      }
    },

    updateTimesheetRequest: (state, action: PayloadAction<any>) => {
      if (action) state.updateTimesheetStatus = RequestStatus.REQUESTING;
    },
    updateTimesheetSuccess: (state, action: PayloadAction<any>) => {
      if (action) {
        state.updateTimesheetStatus = RequestStatus.SUCCESS;
        state.tagList = action.payload;
      }
    },
    updateTimesheetFail: (state, action: PayloadAction<any>) => {
      if (action) state.updateTimesheetStatus = RequestStatus.ERROR;
    },

    deleteTimesheetRequest: (state, action: PayloadAction<any>) => {
      if (action) state.deleteTimesheetStatus = RequestStatus.REQUESTING;
    },
    deleteTimesheetSuccess: (state, action: PayloadAction<any>) => {
      if (action) state.deleteTimesheetStatus = RequestStatus.SUCCESS;
    },
    deleteTimesheetFail: (state, action: PayloadAction<any>) => {
      if (action) state.deleteTimesheetStatus = RequestStatus.ERROR;
    },

    resetTimesheetRequestStatus: state => {
      state.getTimesheetStatus = RequestStatus.IDLE;
      state.addTimesheetStatus = RequestStatus.IDLE;
      state.deleteTimesheetStatus = RequestStatus.IDLE;
      state.updateTimesheetStatus = RequestStatus.IDLE;
      state.approveRequestStatus = RequestStatus.IDLE;
      state.requestApprovalStatus = RequestStatus.IDLE;
      state.cancelRequestStatus = RequestStatus.IDLE;
      state.errorMess = [];
    },
  },
});

export default slice.actions;

export const useTimesheetSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: timesheetSaga });
  return { actions: slice.actions };
};
